import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BuildingIcon,
  ClipboardListIcon,
  ClockIcon,
  HomeIcon,
  MenuIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { usesFieldHome } from '../lib/fieldHome'

type Tab = {
  nameKey: 'home' | 'jobs' | 'clock' | 'properties'
  href: string
  icon: React.ComponentType<{ className?: string; strokeWidth?: number | string }>
  permission?: string | string[]
  match?: (pathname: string) => boolean
}

interface MobileBottomNavProps {
  onOpenMenu: () => void
}

/** Height of the tab row (excludes safe-area inset). Keep in sync with Layout main pad. */
export const MOBILE_BOTTOM_NAV_HEIGHT = '3.75rem'

export function MobileBottomNav({ onOpenMenu }: MobileBottomNavProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { profile } = useAuth()
  const { canAny, isSuperAdmin } = usePermissions()
  const fieldHome = usesFieldHome(profile?.role, isSuperAdmin)
  const propertiesHref = fieldHome ? '/my-properties' : '/properties'

  const tabs: Tab[] = [
    {
      nameKey: 'home',
      href: '/dashboard',
      icon: HomeIcon,
      permission: 'dashboard.view',
      match: (p) =>
        p === '/' ||
        p === '/dashboard' ||
        p === '/my-day' ||
        p.startsWith('/my-day/'),
    },
    {
      nameKey: 'jobs',
      href: '/jobs',
      icon: ClipboardListIcon,
      permission: 'jobs.view',
      match: (p) => p === '/jobs' || p.startsWith('/jobs/'),
    },
    {
      nameKey: 'clock',
      href: '/time-clock',
      icon: ClockIcon,
      permission: 'time.clock',
      match: (p) => p === '/time-clock' || p.startsWith('/time-clock/'),
    },
    {
      nameKey: 'properties',
      href: propertiesHref,
      icon: BuildingIcon,
      permission: 'properties.view',
      match: (p) =>
        p === '/my-properties' ||
        p.startsWith('/my-properties/') ||
        p === '/properties' ||
        p.startsWith('/properties/'),
    },
  ]

  const visible = tabs.filter((tab) => {
    if (!tab.permission) return true
    const perms = Array.isArray(tab.permission) ? tab.permission : [tab.permission]
    return canAny(perms)
  })

  const tabCount = visible.length + 1 // + More

  return (
    <nav
      className="shell-mobile fixed bottom-0 inset-x-0 z-40 w-full flex-col bg-white border-t border-gray-200 shadow-[0_-2px_12px_rgba(15,23,42,0.06)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label={t('nav.fieldNav')}
    >
      <div
        className="grid w-full items-stretch px-0 pt-0.5"
        style={{
          minHeight: MOBILE_BOTTOM_NAV_HEIGHT,
          gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))`,
        }}
      >
        {visible.map((tab) => {
          const Icon = tab.icon
          const active = tab.match
            ? tab.match(location.pathname)
            : location.pathname.startsWith(tab.href)
          return (
            <Link
              key={tab.href}
              to={tab.href}
              aria-current={active ? 'page' : undefined}
              className={`relative flex min-w-0 flex-col items-center justify-center gap-0.5 px-0.5 py-1.5 select-none transition-colors ${
                active ? 'text-blue-700' : 'text-gray-500 active:bg-gray-50'
              }`}
            >
              {active && (
                <span
                  className="absolute top-0 inset-x-4 h-[3px] rounded-b-full bg-blue-600"
                  aria-hidden
                />
              )}
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-xl ${
                  active ? 'bg-blue-100' : ''
                }`}
              >
                <Icon
                  className={`h-[22px] w-[22px] ${active ? 'text-blue-700' : 'text-gray-500'}`}
                  strokeWidth={active ? 2.5 : 2.15}
                />
              </span>
              <span
                className={`max-w-full truncate text-[10px] leading-none ${
                  active ? 'font-bold text-blue-700' : 'font-medium text-gray-500'
                }`}
              >
                {t(`nav.${tab.nameKey}`)}
              </span>
            </Link>
          )
        })}
        <button
          type="button"
          onClick={onOpenMenu}
          className="relative flex min-w-0 flex-col items-center justify-center gap-0.5 px-0.5 py-1.5 text-gray-500 select-none active:bg-gray-50"
          aria-label={t('nav.more')}
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl">
            <MenuIcon className="w-[22px] h-[22px] text-gray-500" strokeWidth={2.15} />
          </span>
          <span className="max-w-full truncate text-[10px] font-medium leading-none text-gray-500">
            {t('nav.more')}
          </span>
        </button>
      </div>
    </nav>
  )
}
